
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddProduct.css'; // Reusing product form styles
import Swal from 'sweetalert2';
import axios from 'axios';
import { UPDATE_PROJECT_CATEGORY_API, VIEW_PROJECT_CATEGORY_API } from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditProjectCategory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [formData, setFormData] = useState({
        categoryName: '',
        status: 'active'
    });

    useEffect(() => {
        const fetchCategoryData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');

                // Fetch from API
                const response = await axios.get(VIEW_PROJECT_CATEGORY_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const c = response.data.data;
                    setFormData({
                        categoryName: c.categoryName || '',
                        status: c.status || 'active'
                    });
                } else {
                    console.error('Failed to fetch category details:', response.data.message);
                    navigate('/project-categories');
                }
            } catch (error) {
                console.error('Error fetching category details:', error);
                navigate('/project-categories');
            } finally {
                setFetchingData(false);
            }
        };

        fetchCategoryData();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;

        const result = await Swal.fire({
            title: 'Update Category?',
            text: 'Do you want to save changes to this category?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_PROJECT_CATEGORY_API(id), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Category updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/project-categories');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update category');
                }
            } catch (error) {
                console.error('Error updating category:', error);
                const errorData = error.response?.data?.error || 'An error occurred while updating the category';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating category..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/project-categories')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Category Details' : 'Edit Project Category'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>Category Information</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Category Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="categoryName"
                                value={formData.categoryName}
                                onChange={handleChange}
                                placeholder="e.g. Residential"
                                disabled={isViewMode}
                            />
                        </div>

                        <div className="form-group">
                            <label>Status <span className="required-star">*</span></label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isViewMode}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/project-categories')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Category
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditProjectCategory;
